/**
 * Der Lager-Baum (ADR 0013 §13, ADR 0016) -- aus der lokalen Kopie, also auch
 * offline.
 *
 * **Feste Orte** stehen, wie sie in den Stammdaten angelegt sind. **Packstücke
 * hängen dort, wo sie gebucht sind** -- die Gitterbox auf dem LKW, solange sie
 * auf dem LKW ist. Ist ein Packstück draußen (im Einsatz, verliehen), steht es
 * nicht im Baum. An seinem Stammplatz fehlt es dann, und dort steht es als
 * „fehlt“ mit dem Ort, an dem es gerade ist (so vom Nutzer entschieden).
 */

import { uebergeordneterOrt, type Lage } from "@thw/core-domain";
import type { Lagerort } from "@thw/contracts";
import type { Bestand } from "./bestand.js";
import { vereinheitlichen } from "./suche.js";
import { liegtIn } from "./verschieben.js";

export interface LagerKnoten {
  lagerort: Lagerort;
  kinder: LagerKnoten[];
  /** Artikel darin, mit allem in Plätzen und Kisten darunter -- ohne die Kisten selbst. */
  anzahl: number;
}

/** Ein Packstück, das gerade draußen ist, hängt an keinem Ort. */
function draussen(bestand: Bestand, l: Lagerort): boolean {
  if (l.artikel_id === null) return false;
  const z = bestand.zustaende.get(l.artikel_id);
  return z !== undefined && (z.verbleib === "im_einsatz" || z.verbleib === "ausgeliehen");
}

/** Ist das ein Packstück, also ein Artikel und zugleich ein Ort? */
function packstueckArtikel(bestand: Bestand): Set<string> {
  const ids = new Set<string>();
  for (const l of bestand.lagerorte.values()) if (l.artikel_id !== null) ids.add(l.artikel_id);
  return ids;
}

/**
 * Der ganze Baum, oben die Orte, „Ohne festen Platz“ zuletzt.
 *
 * Stillgelegte Orte stehen nur da, solange noch etwas in ihnen liegt -- sonst
 * verschwände es aus dem Baum. Wer nach widerstreitenden Offline-Buchungen im
 * Kreis steckt (A in B, B in A), hängt oben, damit nichts verloren geht.
 */
export function lagerBaum(bestand: Bestand): LagerKnoten[] {
  const lageVon = (id: string): Lage | undefined => bestand.zustaende.get(id);
  const kisten = packstueckArtikel(bestand);

  // „Unbekannt“ zählt, was dort steht -- verloren und gestohlen, auch in
  // verlorenen Kisten (ADR 0020). Überall sonst zählt Archiviertes nicht.
  const unbekannt = [...bestand.lagerorte.values()].find((l) => l.unbekannt);
  const direkt = new Map<string, number>();
  for (const [artikel_id, z] of bestand.zustaende) {
    if (z.lagerort_id === null || kisten.has(artikel_id)) continue;
    if (z.archiviert && (unbekannt === undefined || !liegtIn(bestand, z.lagerort_id, unbekannt.id)))
      continue;
    // Was mit seiner Kiste draußen ist, liegt nicht im Lager.
    if (z.verbleib !== "im_lager") continue;
    direkt.set(z.lagerort_id, (direkt.get(z.lagerort_id) ?? 0) + 1);
  }

  const kinderVon = new Map<string | null, Lagerort[]>();
  for (const l of bestand.lagerorte.values()) {
    if (draussen(bestand, l)) continue;
    const oben = uebergeordneterOrt(l, lageVon);
    const liste = kinderVon.get(oben);
    if (liste === undefined) kinderVon.set(oben, [l]);
    else liste.push(l);
  }

  const besucht = new Set<string>();
  const bauen = (l: Lagerort): LagerKnoten | null => {
    if (besucht.has(l.id)) return null;
    besucht.add(l.id);
    const kinder = (kinderVon.get(l.id) ?? [])
      .map(bauen)
      .filter((k): k is LagerKnoten => k !== null)
      .sort((a, b) => a.lagerort.kennung.localeCompare(b.lagerort.kennung, "de"));
    const anzahl = (direkt.get(l.id) ?? 0) + kinder.reduce((s, k) => s + k.anzahl, 0);
    if (!l.aktiv && anzahl === 0 && kinder.length === 0) return null;
    return { lagerort: l, kinder, anzahl };
  };

  const oben = (kinderVon.get(null) ?? []).map(bauen);
  // Im Kreis: Was bisher nicht erreicht wurde, kommt nach oben.
  for (const l of bestand.lagerorte.values()) {
    if (!besucht.has(l.id) && !draussen(bestand, l)) oben.push(bauen(l));
  }
  return oben
    .filter((k): k is LagerKnoten => k !== null)
    .sort(
      (a, b) =>
        // Unten erst „Ohne festen Platz“, dann „Unbekannt“.
        rangUnten(a.lagerort) - rangUnten(b.lagerort) ||
        a.lagerort.kennung.localeCompare(b.lagerort.kennung, "de"),
    );
}

function rangUnten(l: Lagerort): number {
  if (l.unbekannt) return 2;
  return l.standardziel ? 1 : 0;
}

/** Was die Suche im Baum ergibt. */
export interface LagerTreffer {
  /** Knoten, die zu sehen sind -- die Treffer und der Weg zu ihnen. */
  sichtbar: ReadonlySet<string>;
  /** Knoten, deren Kennung oder Bezeichnung passt -- sie zeigen ihren ganzen Inhalt. */
  orte: ReadonlySet<string>;
  /** Artikel, auf die die Suche passt. In den übrigen Knoten steht nur noch das. */
  artikel: ReadonlySet<string>;
}

function passt(woerter: readonly string[], text: string): boolean {
  const t = vereinheitlichen(text);
  return woerter.every((w) => t.includes(w));
}

/**
 * „Im Lager suchen“: Orte nach Kennung und Bezeichnung, Artikel nach
 * Bezeichnung und Nummer. `null` ohne Eingabe -- dann gilt der ganze Baum.
 */
export function lagerSuchen(
  bestand: Bestand,
  baum: readonly LagerKnoten[],
  eingabe: string,
): LagerTreffer | null {
  const woerter = vereinheitlichen(eingabe.trim()).split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return null;

  const kisten = packstueckArtikel(bestand);
  const unbekannt = [...bestand.lagerorte.values()].find((l) => l.unbekannt);
  const artikel = new Set<string>();
  const artikelIn = new Set<string>();
  for (const a of bestand.artikel.values()) {
    if (kisten.has(a.id)) continue;
    if (!passt(woerter, `${a.bezeichnung} ${a.inventarnummer}`)) continue;
    const z = bestand.zustaende.get(a.id);
    if (z?.lagerort_id === null || z === undefined) continue;
    // Verlorenes findet die Suche dort, wo es steht: unter „Unbekannt“.
    if (z.archiviert && (unbekannt === undefined || !liegtIn(bestand, z.lagerort_id, unbekannt.id)))
      continue;
    artikel.add(a.id);
    artikelIn.add(z.lagerort_id);
  }

  const sichtbar = new Set<string>();
  const orte = new Set<string>();
  const pruefen = (k: LagerKnoten): boolean => {
    const name = passt(woerter, `${k.lagerort.kennung} ${k.lagerort.bezeichnung}`);
    if (name) orte.add(k.lagerort.id);
    let zeigen = name || artikelIn.has(k.lagerort.id);
    for (const kind of k.kinder) if (pruefen(kind)) zeigen = true;
    if (zeigen) sichtbar.add(k.lagerort.id);
    return zeigen;
  };
  for (const k of baum) pruefen(k);
  return { sichtbar, orte, artikel };
}
